import type { IWatermarkElement } from '@/types/watermarks.types';
import type { TransformRef } from '../watermark.types';
import { distanceBetweenTranslations2D, getDomMatrixAt, getDomMatrixFromStyles } from './matrix-utils';
import type React from 'react';

const nodeToTypeMap: Record<string, string> = {
  'top-right-node': 'top-right',
  'top-left-node': 'top-left',
  'bottom-right-node': 'bottom-right',
  'bottom-left-node': 'bottom-left',
  'rotate-node': 'rotate',
};

export const onMouseDown =
  (
    parentRef: React.RefObject<HTMLDivElement | null>,
    elementRefs: React.RefObject<Map<string, HTMLDivElement | null>>,
    wmElement: IWatermarkElement,
    transforms: React.RefObject<TransformRef>,
    debugDot: React.RefObject<HTMLDivElement | null>
  ) =>
  (e: React.PointerEvent<HTMLDivElement>) => {
    // get target element
    transforms.current.selectedElement = wmElement;
    transforms.current.selectedDiv = elementRefs.current.get(wmElement.uuid);
    if (!transforms.current.selectedDiv || !parentRef.current) return;

    const parentRect = parentRef.current?.getBoundingClientRect();

    // init params
    transforms.current.parentX = parentRect.left;
    transforms.current.parentY = parentRect.top;
    transforms.current.mouseDown = true;
    transforms.current.startX = e.pageX - parentRect.left;
    transforms.current.startY = e.pageY - parentRect.top;
    transforms.current.transform = getDomMatrixFromStyles(transforms.current.selectedDiv);

    // target is NOT the visible text/image element, it's the interactive box
    if (e.target instanceof HTMLDivElement) {
      // get current scale of the wrapper which resizes down to fit the 16/9 canvas
      const inverseScale = 1 / transforms.current.pageScale;

      // get the half-size for use as the center point of the selected div
      transforms.current.selectedDivOffsetX = transforms.current.selectedDiv.offsetWidth / 2;
      transforms.current.selectedDivOffsetY = transforms.current.selectedDiv.offsetHeight / 2;

      // get the page offset of the selected handle
      const rect = e.target.getBoundingClientRect();

      // create a transform for the selected handle
      transforms.current.transformHandle = getDomMatrixAt(
        (rect.left - parentRect.left) * inverseScale,
        (rect.top - parentRect.top) * inverseScale
      );

      debugDot.current?.style.setProperty('transform', transforms.current.transformHandle.toString());

      // get the current distance of the handle from the root transform in pixels
      transforms.current.transformScale = distanceBetweenTranslations2D(
        transforms.current.transformHandle,
        transforms.current.transform
      );

      // create a new transform at the center of the target element - the root transform is the top-left corner of that element
      // but we need it to rotate and scale aroundt he middle
      transforms.current.centerTransform = getDomMatrixAt(
        transforms.current.transform.e + transforms.current.selectedDivOffsetX,
        transforms.current.transform.f + transforms.current.selectedDivOffsetY
      );

      // set the target action we're performing based on the selected handle node
      transforms.current.transformType = nodeToTypeMap[e.target.id] ?? 'box';
    }
  };
